import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import HelpTooltip from 'help/HelpTooltip';
import Label from 'ui-library/bs/Label';
import TextArea from 'ui-library/bs/TextArea';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import TextInput from 'ui-library/inputs/TextInput';
import {InputChangeEvent} from 'util/Types';

// keep in sync with request_v2_definition_table.py
export const MAX_DESCRIPTION_LENGTH = 1024;
export const MAX_REQUEST_DEFINITION_PREFIX_LENGTH = 32;

export function maybeStrLengthValidationMessage(inputStr: string, maxLength: number): string | null {
  const strLength = inputStr.trim().length;
  if (strLength > maxLength) {
    return strLengthMessage(strLength, maxLength);
  }
  return null;
}

function strLengthMessage(strLength: number, maxLength: number): string {
  return `${strLength} of ${maxLength} allowed characters`;
}

export function RequestTemplateInputGroup({children}: React.PropsWithChildren<{}>): JSX.Element {
  return <div className={css(styles.requestTemplateInputGroup)}>{children}</div>;
}

interface RequestV2DefinitionDescriptionFieldProps {
  description: string;
  onChangeHandler: React.ChangeEventHandler<HTMLTextAreaElement>;
  errorMessage?: string;
  dataTestComponentName: string;
}

export function RequestV2DefinitionDescriptionField(
  props: RequestV2DefinitionDescriptionFieldProps
): JSX.Element {
  return (
    <RequestTemplateInputGroup>
      <Label>Description</Label>
      <HelpTooltip
        text="This description helps users know what type of request this template is for."
        className="u-mini-spacing-left"
      />
      <TextArea
        value={props.description}
        placeholder="Enter a description"
        onChange={props.onChangeHandler}
        dataTestAttrs={{
          component: props.dataTestComponentName,
          element: 'descriptionField',
        }}
        rows={3}
      />
      <>
        {props.errorMessage ? (
          <ErrorMessage
            error={props.errorMessage}
            dataTestAttrs={{
              component: props.dataTestComponentName,
              element: 'descriptionFieldErrorMessage',
            }}
          />
        ) : (
          <div className={css(styles.fieldDescription)}>
            {strLengthMessage(props.description.trim().length, MAX_DESCRIPTION_LENGTH)}
          </div>
        )}
      </>
    </RequestTemplateInputGroup>
  );
}

interface RequestV2DefinitionPrefixFieldProps {
  prefix: string;
  onChangeHandler: (e: InputChangeEvent) => void;
  errorMessage?: string;
  dataTestComponentName: string;
}

export function RequestV2DefinitionPrefixField(props: RequestV2DefinitionPrefixFieldProps): JSX.Element {
  return (
    <RequestTemplateInputGroup>
      <Label required={true}>Request ID prefix</Label>
      <HelpTooltip
        text="This prefix will help create unique IDs for requests made with this template."
        className="u-mini-spacing-left"
      />
      <TextInput
        value={props.prefix}
        hasError={props.errorMessage != null}
        onChange={props.onChangeHandler}
        placeholder="Enter a prefix"
        dataTestAttrs={{
          component: props.dataTestComponentName,
          element: 'prefixField',
        }}
      />
      <>
        {props.errorMessage ? (
          <ErrorMessage
            error={props.errorMessage}
            dataTestAttrs={{
              component: props.dataTestComponentName,
              element: 'prefixFieldErrorMessage',
            }}
          />
        ) : (
          <div className={css(styles.fieldDescription)}>
            No special characters or spaces, can't end with a number
          </div>
        )}
      </>
    </RequestTemplateInputGroup>
  );
}

const styles = StyleSheet.create({
  requestTemplateInputGroup: {
    marginBottom: 16,
  },
  fieldDescription: {
    color: GRAY_SEMIDARK,
  },
});
